// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import buttonSizeVariants from "@insite/mobius/Button/buttonSizeVariants";
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import OrderHeader from "@store/Library/Components/Toolkit/Order/OrderHeader";
import constants from "@store/constants";
import Button from "@store/MobiusOverrides/Button/Button";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useCurrentPage } from "@toolkit/Framework/Store/Data/Pages/PageHooks";
import { usePageLinkByPageType } from "@toolkit/Framework/Store/Links/LinksHooks";
import React, { FC } from "react";
import { useSelector } from "react-redux";
import { css } from "styled-components";

const enum fields {
    firstButtonText = "firstButtonText",
    firstButtonLink = "firstButtonLink",
    firstButtonOpenInNewWindow = "firstButtonOpenInNewWindow",
    secondButtonText = "secondButtonText",
    secondButtonLink = "secondButtonLink",
    secondButtonOpenInNewWindow = "secondButtonOpenInNewWindow",
    thirdButtonText = "thirdButtonText",
    thirdButtonLink = "thirdButtonLink",
    thirdButtonOpenInNewWindow = "thirdButtonOpenInNewWindow",
}

export interface SignedInPageActionsProps extends WidgetProps {
    fields: {
        [fields.firstButtonText]: string;
        [fields.firstButtonLink]: LinkFieldValue;
        [fields.firstButtonOpenInNewWindow]: boolean;
        [fields.secondButtonText]: string;
        [fields.secondButtonLink]: LinkFieldValue;
        [fields.secondButtonOpenInNewWindow]: boolean;
        [fields.thirdButtonText]: string;
        [fields.thirdButtonLink]: LinkFieldValue;
        [fields.thirdButtonOpenInNewWindow]: boolean;
    };
}

export interface SignedInPageActionsStyles {
    buttonStyle?: ButtonPresentationProps;
    styledWrapperStyle?: InjectableCss;
}

export const SignedInPageActionsStyles: SignedInPageActionsStyles = {
    styledWrapperStyle: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: space-around;
            background: rgba(255, 255, 255, 0.7);
            border-radius: ${pxToRem(20)};
            height: ${pxToRem(250)};
            padding: ${pxToRem(20)};
            ${breakpointValues(["100%", "100%", pxToRem(645), null, null], "width")};
            ${breakpoint("lg", "down")} {
                margin-top: ${pxToRem(16)};
            }
        `,
    },
    buttonStyle: {
        sizeVariant: "large",
    },
};

const styles = SignedInPageActionsStyles;

const SignedInPageActions: FC<SignedInPageActionsProps> = ({ fields }) => {
    const history = useHistory();
    const { isInShell } = useShellContext();
    const firstLink = useGetLink(fields.firstButtonLink);
    const secondLink = useGetLink(fields.secondButtonLink);
    const thirdLink = useGetLink(fields.thirdButtonLink);

    const onClick = (
        link: Readonly<PageLinkModel> | { url: string | undefined; title: string | undefined },
        openInNewWindow: boolean,
    ) => {
        if (!link.url) {
            return;
        }

        if (link.url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            if (openInNewWindow) {
                window.open(link.url, "_blank");
            } else {
                window.location.href = link.url;
            }
        } else if (openInNewWindow) {
            window.open(link.url, "_blank");
        } else {
            history.push(link.url);
        }
    };

    const firstOnClick = () => {
        onClick(firstLink, fields.firstButtonOpenInNewWindow);
    };

    const secondOnClick = () => {
        onClick(secondLink, fields.secondButtonOpenInNewWindow);
    };

    const thirdOnClick = () => {
        onClick(thirdLink, fields.thirdButtonOpenInNewWindow);
    };

    return (
        <StyledWrapper {...styles.styledWrapperStyle}>
            <Button {...styles.buttonStyle} onClick={firstOnClick}>
                {fields.firstButtonText}
            </Button>
            <Button {...styles.buttonStyle} onClick={secondOnClick}>
                {fields.secondButtonText}
            </Button>
            <Button {...styles.buttonStyle} onClick={thirdOnClick}>
                {fields.thirdButtonText}
            </Button>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: SignedInPageActions,
    definition: {
        displayName: "Signed In Page Actions",
        group: "Sign In",
        allowedContexts: ["SignInPage"],
        fieldDefinitions: [
            {
                name: fields.firstButtonText,
                displayName: "First Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "What Can the Portal Do for You?",
            },
            {
                name: fields.firstButtonLink,
                displayName: "First Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "Instruction Videos",
                    type: "Page",
                },
                fieldType: "General",
            },
            {
                name: fields.firstButtonOpenInNewWindow,
                displayName: "Open First Button in New Window?",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.secondButtonText,
                displayName: "Second Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Is the Portal Available for You?",
            },
            {
                name: fields.secondButtonLink,
                displayName: "Second Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "Store Locator",
                    type: "Page",
                },
                fieldType: "General",
            },
            {
                name: fields.secondButtonOpenInNewWindow,
                displayName: "Open Second Button in New Window?",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.thirdButtonText,
                displayName: "Third Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Is the Portal Available for You?",
            },
            {
                name: fields.thirdButtonLink,
                displayName: "Third Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "Contact Us",
                    type: "Page",
                },
                fieldType: "General",
            },
            {
                name: fields.thirdButtonOpenInNewWindow,
                displayName: "Open Third Button in New Window?",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
